import React, {useEffect, useState} from 'react'
import DateTimePicker from '../DateAndTimePicker'
import * as Yup from 'yup'
import {FiClock, FiEdit, FiCalendar, FiType, FiInfo} from 'react-icons/fi'
import PrimaryButton from '../common/buttons/PrimaryButton'
import Astericsign from '../../../user/components/forms/Astericsign'

interface EditPatientTrackedTimeProps {
  show: boolean
  initialData: any
  onSave: (updatedData: any) => void
  onCancel: () => void
  isSubmitting?: boolean // Optional prop to control loading state from parent
}

const EditPatientTrackedTime: React.FC<EditPatientTrackedTimeProps> = ({
  show,
  initialData,
  onSave,
  onCancel,
  isSubmitting = false,
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [description, setDescription] = useState('')
  const [trackingType, setTrackingType] = useState<'auto' | 'manual'>('manual')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)

  // Yup schema: only fromDate, toDate, description, trackingType
  const validationSchema = Yup.object().shape({
    fromDate: Yup.date()
      .required('Start time is required')
      .typeError('Start time is required')
      .max(new Date(), 'Start cannot be in the future'),
    toDate: Yup.date()
      .required('End time is required')
      .typeError('End time is required')
      .min(Yup.ref('fromDate'), 'End time must be after start time')
      .max(new Date(), 'End cannot be in the future'),
    description: Yup.string()
      // .required('Description is required')
      .max(1024, 'Description cannot exceed 1024 characters'),
    trackingType: Yup.mixed<'auto' | 'manual'>().oneOf(['auto', 'manual']),
  })

  // Populate on open
  useEffect(() => {
    if (show && initialData) {
      setFromDate(initialData.from ? new Date(initialData.from) : null)
      setToDate(initialData.to ? new Date(initialData.to) : null)
      setDescription(initialData.description || '')
      setTrackingType(initialData.tracking_type || 'manual')
      setErrors({})
      setIsLoading(false) // Reset loading state when modal opens
    }
  }, [show, initialData])


  const handleSave = async () => {
    // Prevent multiple clicks
    if (isLoading) return
    
    setIsLoading(true)
    setErrors({}) // Clear previous errors

    try {
      await validationSchema.validate(
        {fromDate, toDate, description, trackingType},
        {abortEarly: false}
      )
      const durationMinutes =
        fromDate && toDate ? Math.round((toDate.getTime() - fromDate.getTime()) / 60000) : 0

      // build a "HH:MM:SS" string
      const hours = Math.floor(durationMinutes / 60)
      const minutes = durationMinutes % 60
      const timeSpentString =
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
      
      // Add a minimum delay to ensure loader is visible
      await new Promise(resolve => setTimeout(resolve, 500))
      
      onSave({
        from: fromDate!.toISOString(),
        to: toDate!.toISOString(),
        time_spent: timeSpentString,
        description,
        tracking_type: trackingType,
      })
      
      // Don't reset loading state here - let parent component handle it
      // Either through modal close or through a success callback
      
    } catch (err: any) {
      setIsLoading(false) // Only reset loading on validation errors
      if (err.name === 'ValidationError') {
        const newErrors: Record<string, string> = {}
        err.inner.forEach((e: any) => {
          if (e.path) newErrors[e.path] = e.message
        })
        setErrors(newErrors)
      }
    }
  }

  if (!show) return null

  return (
    <div
      className='modal fade show'
      style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)'}}
    >
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content border-0 shadow-lg' style={{borderRadius: 12}}>
          {/* Header */}
          <div
            className='modal-header'
          >
            <h5 className='modal-title d-flex align-items-center gap-2 text-black'>
              <FiEdit /> Edit Time Tracking Entry
            </h5>
            <button type='button' className='btn-close' 
            onClick={onCancel} />
          </div>

          {/* Body */}
          <div className='modal-body py-4'>
            <div className='row g-4'>
              {/* Start */}
              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center gap-2 text-secondary'>
                  <FiCalendar /> Start Time <Astericsign />
                </label>
                <DateTimePicker
                  isEdit={true}
                  value={fromDate}
                  onChange={(date) => setFromDate(date)}
                  maxDate={new Date()} // e.g. current time
                  className={`form-control ${errors.fromDate ? 'is-invalid' : ''}`}
                />
                {errors.fromDate && (
                  <div className='invalid-feedback d-flex align-items-center gap-2'>
                    <FiInfo /> {errors.fromDate}
                  </div>
                )}
              </div>

              {/* End */}
              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center gap-2 text-secondary'>
                  <FiCalendar /> End Time <Astericsign />
                </label>
                <DateTimePicker
                  isEdit={true}
                  value={toDate}
                  onChange={(date) => setToDate(date)}
                  maxDate={new Date()} // e.g. current time
                  className={`form-control ${errors.toDate ? 'is-invalid' : ''}`}
                />
                {errors.toDate && (
                  <div className='invalid-feedback d-flex align-items-center gap-2'>
                    <FiInfo /> {errors.toDate}
                  </div>
                )}
              </div>

              {/* Computed Duration */}
              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center gap-2 text-secondary'>
                  <FiClock /> Computed Duration 
                </label>
                <div className='form-control bg-light'>
                  {
                    fromDate && toDate
                      ? `${Math.round((toDate.getTime() - fromDate.getTime()) / 60000)} minutes`
                      : ''
                  }
                </div>
              </div>

              {/* Tracking Method */}
              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center gap-2 text-secondary'>
                  <FiType /> Tracking Method
                </label>
                <div className='form-control bg-light'>
                  {trackingType}
                </div>
                {/* <select
                  className='form-select'
                  value={trackingType}
                  onChange={(e) => setTrackingType(e.target.value as 'auto' | 'manual')}
                >
                  <option value='auto'>Automatic</option>
                  <option value='manual'>Manual</option>
                </select> */}
              </div>

              {/* Description */}
              <div className='col-12'>
                <label className='form-label d-flex align-items-center gap-2 text-secondary'>
                  <FiInfo /> Notes
                </label>
                <div className='position-relative'>
                  <textarea
                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='Enter activity description...'
                    maxLength={1024}
                  />
                  <div className='d-flex justify-content-end text-muted'>
                    {description.length}/1024 characters
                  </div>
                </div>
                {errors.description && (
                  <div className='invalid-feedback d-flex align-items-center gap-2'>
                    <FiInfo /> {errors.description}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='modal-footer border-top-0 bg-light'>
            <PrimaryButton
            color='secondary'
            className='text-white'
            onClick={onCancel}
            disabled={isLoading}>
              Cancel
            </PrimaryButton>
            <PrimaryButton
              className='px-4 d-flex align-items-center gap-2'
              onClick={handleSave}
              disabled={isLoading}
            >
              {!isLoading ? (
                <>
                  <FiEdit /> Update Entry
                </>
              ) : (
                <>
                  <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />{' '}
                  Updating...
                </>
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditPatientTrackedTime
